import type { DetectedPreset } from '@/core/preset-selection'
import type { LayoutDetection } from '@/presets/types'
import { findArtaleHud, artaleHudRegions } from '@/presets/artale/layout-locator'
import { findMxdcHudScenes } from '@/presets/mxdc/layout-locator'
import { findGcwHud, gcwHudRegions } from '@/presets/gcw/layout-locator'
import { findWorldsHud, worldsHudRegions } from '@/presets/worlds/layout-locator'

export interface UiProbeResult {
  kind: DetectedPreset | 'unknown' | 'ambiguous'
  layout?: LayoutDetection
  reason?: 'timeout'
}

/** A search deadline cannot erase positive evidence from another family. */
export function probeGameUi(frame: ImageData): UiProbeResult {
  const options = { deadlineAt: Date.now() + 5_000 }
  let reason: UiProbeResult['reason']
  const search = <T>(run: () => T[]): T[] => {
    try { return run() }
    catch (error) {
      if (!(error instanceof DOMException && error.name === 'TimeoutError')) throw error
      reason = 'timeout'
      return []
    }
  }
  // Check distinctive, inexpensive structures before the broad classic search.
  const gcw = search(() => findGcwHud(frame, options))
  const artale = search(() => findArtaleHud(frame, options))
  const worlds = search(() => findWorldsHud(frame, options))
  const classic = search(() => findMxdcHudScenes(frame, options))
  const gcwLayouts = gcw.map(anchor => gcwHudRegions(frame, anchor))
  const distinctClassic = classic.filter(layout => !gcwLayouts.some((gcwLayout, index) =>
    (['hp', 'mp', 'exp'] as const).some(metric => {
      const a = gcwLayout.regions[metric]?.textRect, b = layout.regions[metric]?.textRect
      return a && b && Math.abs(a.x - b.x) * frame.width < 8 * gcw[index]!.sx
        && Math.abs(a.y - b.y) * frame.height < 8 * gcw[index]!.sy
    })))
  const scenes = [
    ...gcwLayouts.map(layout => ({ kind: 'gcw' as const, layout })),
    ...artale.map(anchor => ({ kind: 'artale' as const, layout: artaleHudRegions(frame, anchor) })),
    ...worlds.map(anchor => ({ kind: 'worlds' as const, layout: worldsHudRegions(frame, anchor) })),
    ...distinctClassic.map(layout => ({ kind: 'mxdc' as const, layout })),
  ]
  const result: UiProbeResult = scenes.length > 1 ? { kind: 'ambiguous' } : scenes[0] ?? { kind: 'unknown' }
  return reason ? { ...result, reason } : result
}
